import { ModalLayer } from "#skyward_modalLayer";

type TabControlElements =
{
    before: JQuery<HTMLElement>;
    after: JQuery<HTMLElement>;
};

interface FocusManagerEvent extends JQuery.FocusEvent
{
    generatedByFocusManager: true
};

const registeredLayers: ModalLayer[] = [];
const ignoreElementClass = "js-dialogFocusManagerIgnore";

// This is a template that will be used to create the tab control elements within modal layers
const tabControlTemplate = $("<div>", { tabindex: 0 });

/**
* Map the dialog Container with the before and after jQuery tab control elements
*/
const tabControlElements = new WeakMap<ModalLayer, TabControlElements>();

// use focusin because it bubbles
$(document).on("focusin", focusHandler);

/**
 * Registers a new ModalLayer with the DialogFocusManager and adds tab control elements
 *
 * @param layer - A ModalLayer to register
 */
export function registerLayer(layer: ModalLayer)
{
    registeredLayers.push(layer);
    addTabControlElements(layer);
}

/**
 * Unregisters a ModalLayer from the DialogFocusManager and removes tab control elements
 *
 * @param layer - A ModalLayer to unregister from the DialogFocusManager
 * @param restoreFocus - Should focus be moved to the originalActiveElement of the ModalLayer
 */
export function unregisterLayer(layer: ModalLayer, restoreFocus?: boolean)
{
    const layerIndex = registeredLayers.indexOf(layer);
    if (layerIndex > -1)
    {
        registeredLayers.splice(layerIndex, 1);
    }
    if (tabControlElements.has(layer))
    {
        const tabControls = tabControlElements.get(layer)!;
        tabControls.before.remove();
        tabControls.after.remove();
        tabControlElements.delete(layer);
    }
    if (restoreFocus === true && document.activeElement !== layer.originalActiveElement)
    {
        $(layer.originalActiveElement).trigger("focus");
    }
}

/**
 * Sets focus to the initial element within a ModalLayer
 *
 * @param layer - A ModalLayer to use as a basis for finding the element to set focus to
 */
export function setFocus(layer: ModalLayer)
{
    const focusElement = getInitialFocusElement(layer);
    // Work around jquery focus bug: https://stackoverflow.com/a/34401217
    // todo (jquery update) See if this can be removed
    const input = focusElement.get(0);
    if (input)
    {
        if (input.matches(".dateTimePickerWrapper") || input.parentElement?.matches(".dateTimePickerWrapper"))
        {
            input.setAttribute("data-suppress-focus", "true");
        }

        // Forces the cursor to be placed at the end of text on inputs
        if (isTextInput(input))
        {
            const length = input.value.length;
            input.setSelectionRange(length, length);
        }

        input.focus();
    }
    return focusElement;
}

/**
 * Toggles if an element should be ignored by the DialogFocusManager
 *
 * @param element - The element to ignore or unignore
 * @param ignore - True if the element should be ignored
 */
export const toggleIgnoreElement = function (element: JQuery<HTMLElement>, ignoreToggle: boolean)
{
    element.toggleClass(ignoreElementClass, ignoreToggle);
}

function isTextInput(element: HTMLElement): element is HTMLInputElement
{
    return element.tagName.toLowerCase() === "input" && (element as HTMLInputElement).type === "text"
}

function focusHandler(event: JQuery.FocusInEvent & { generatedByFocusManager?: boolean })
{
    // we don't want to respond to the focus events we generate
    if (event.generatedByFocusManager)
    {
        return;
    }

    const activeLayer = getActiveLayer();
    if (!activeLayer)
    {
        return;
    }

    const focusElement = $(event.target);
    if (focusElement.length)
    {
        if (focusElement.hasClass(ignoreElementClass))
        {
            return;
        }

        // if focus is within the current dialog, do nothing
        if (activeLayer.contents.get(0)!.contains(focusElement[0]))
        {
            return;
        }

        if (focusElement.closest(".exposedToModalLayer_" + activeLayer.uniqueId).length > 0)
        {
            return;
        }

        // if the focus if coming from inside a Froala Editor popup, do nothing
        if (focusElement.closest(".fr-popup,.fr-modal").length > 0)
        {
            return;
        }
    }

    const tabControls = tabControlElements.get(activeLayer)!;
    let nextFocusElement;

    if (focusElement.is(tabControls.before))
    {
        nextFocusElement = activeLayer.contents.find(":tabbable").last();
    }
    else if (focusElement.is(tabControls.after))
    {
        nextFocusElement = activeLayer.contents.find(":tabbable").first();
    }

    if (typeof nextFocusElement === "undefined" || nextFocusElement.length === 0)
    {
        nextFocusElement = getInitialFocusElement(activeLayer);
    }

    nextFocusElement.trigger(
        {
            type: "focus",
            generatedByFocusManager: true
        } as FocusManagerEvent);
}

function getInitialFocusElement(layer: ModalLayer)
{
    let elementToFocus;
    // Set preserveFocus to true to restore focus to the element that was focused before the layer was rendered. Set it to a jQuery-able value to focus a specific element.
    if (layer.options.preserveFocus === true)
    {
        elementToFocus = $(layer.originalActiveElement).closest(":focusable");
    }
    else if (layer.options.initialFocusElement)
    {
        elementToFocus = layer.container.find($(layer.options.initialFocusElement));
    }

    if (!elementToFocus || elementToFocus.length === 0)
    {
        const modalContent = layer.contents.find(".content");
        const tabbableElements = modalContent.find(":tabbable").not(".ui-disabled").not("[readonly]");
        const containsComplexStructure = modalContent.find("input:not([type='hidden']), textarea, select, table, ul, dl, ol, h1, h2, h3, h4, h5, h6").length > 0;
        const headerTitle = layer.contents.find(".modalHeaderText");

        if (tabbableElements.length > 0)
        {
            elementToFocus = tabbableElements.first();
        }
        else if (containsComplexStructure && headerTitle.length > 0)
        {
            headerTitle.attr("tabindex", "-1");
            elementToFocus = headerTitle.first();
        }
        else
        {
            const tabbableElements = layer.contents.find(":tabbable");
            if (tabbableElements.length > 0)
            {
                elementToFocus = tabbableElements.first();
            }
        }
    }

    // as a last resort, give the content div focus
    if (!elementToFocus || elementToFocus.length === 0)
    {
        elementToFocus = layer.contents.attr("tabindex", "0");
    }
    else
    {
        layer.contents.removeAttr("tabindex");
    }

    return elementToFocus;
}

/**
 * Adds focusable div elements before and after the dialog content.
 * These elements are used to trap focus within the dialog content area.
 *
 * @param layer - A ModalLayer to add the tab control elements to
 */
function addTabControlElements(layer: ModalLayer)
{
    const beforeElement = tabControlTemplate.clone();
    const afterElement = tabControlTemplate.clone();

    beforeElement.insertBefore(layer.contents);
    afterElement.insertAfter(layer.contents);

    tabControlElements.set(layer, { before: beforeElement, after: afterElement });
}

function getActiveLayer()
{
    for (let i = registeredLayers.length - 1; i >= 0; i--)
    {
        if (isDialogActive(registeredLayers[i]))
        {
            return registeredLayers[i];
        }
    }
    return undefined;
}

function isDialogActive(layer: ModalLayer)
{
    return typeof layer === "object" && layer.onscreen && layer.container.length && layer.contents.length && document.body.contains(layer.contents[0]);
}